// React
import type React from 'react';

// Refine
import { useNotification, useTranslation } from '@refinedev/core';

// Mantine
import { Button, Tooltip, useComputedColorScheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

// Icons
import { IconDownload, IconFileExport } from '@tabler/icons-react';

// API
import { useGenerateClaimCSV } from 'api/action';

// Zustand Stores
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';

// Custom Components
import { ClaimActionConfirmDialogModal } from 'pages/claims/components';

import { useIsProvider } from 'hooks';
import {
  convertClaimFiltersToQueryString,
  convertGlobalFilterToQueryString,
  convertSortingToQueryString,
} from 'utils/filterUtils';

// Sentry
import * as Sentry from '@sentry/react';

interface InternalExportTableButtonProps {
  label?: string;
}

export const InternalExportTableButton: React.FC<
  InternalExportTableButtonProps
> = ({ label }) => {
  const toolbarIconSize = 24;
  const toolbarButtonSize = 32;
  const computedColorScheme = useComputedColorScheme('dark');
  const generateCSV = useGenerateClaimCSV();

  const { id: siteID } = useCurrentSiteStore();
  const { providers } = useCurrentUserStore();
  const isProvider = useIsProvider();
  const { pagination } = useClaimPaginationStore();
  const [opened, { open, close }] = useDisclosure(false);
  const { open: openNotification } = useNotification();
  const { translate } = useTranslation();

  const providerIDs = isProvider
    ? providers?.map((provider) => provider.id) || []
    : [];

  const handleExportCSV = async () => {
    const filters = convertClaimFiltersToQueryString(pagination.columnFilters);
    const globalFilter = convertGlobalFilterToQueryString(
      pagination.globalFilter
    );
    const sorting = convertSortingToQueryString(pagination.sorting);
    try {
      const response = await generateCSV.mutateAsync({
        siteID,
        filters,
        globalFilter,
        sorting,
        providerIDs,
      });
      window.open(response.url, '_blank');
    } catch (error) {
      Sentry.captureException(error);
      openNotification({
        message: translate('pages:claims.exportErrorMessage'),
        description: translate('pages:claims.exportErrorDescription'),
        type: 'error',
      });
    } finally {
      close();
    }
  };

  return (
    <>
      <Tooltip
        label={translate('pages:claims.exportDisclaimerTitle')}
        position="top"
      >
        <Button
          onClick={open}
          variant={label ? 'default' : 'subtle'}
          size="xs"
          p={label ? undefined : 0}
          leftSection={label ? <IconDownload size={14} /> : undefined}
          style={{
            width: label ? undefined : toolbarButtonSize,
            height: label ? 28 : toolbarButtonSize,
            minWidth: toolbarButtonSize,
            color: label ? 'var(--mantine-color-text)' : undefined,
          }}
        >
          {label || (
            <IconFileExport
              size={toolbarIconSize}
              color={computedColorScheme === 'dark' ? 'white' : 'black'}
            />
          )}
        </Button>
      </Tooltip>
      <ClaimActionConfirmDialogModal
        open={opened}
        setOpen={close}
        onConfirm={handleExportCSV}
        title={translate('pages:claims.exportDisclaimerTitle')}
        emphasizePhrase={translate('pages:claims.exportDisclaimerHighlight')}
        body={translate('pages:claims.exportDisclaimer')}
      />
    </>
  );
};
